<script setup lang="ts">
import DateKnittedInput from '@/components/features/batches/form-elements/DateKnittedInput.vue'
import SupplierInput from '@/components/features/batches/form-elements/SupplierInput.vue'
import WeightInput from '@/components/features/batches/form-elements/WeightInput.vue'
import MachineNumberInput from '@/components/features/batches/form-elements/MachineNumberInput.vue'
import BayInput from '@/components/features/batches/form-elements/BayInput.vue'
import PurchaseOrderReferenceInput from '@/components/features/batches/form-elements/PurchaseOrderReferenceInput.vue'
import NotesInput from '@/components/features/batches/form-elements/NotesInput.vue'
import type {Batch, BatchRoll} from '@/types/batch-types'
import LastEditedByInput from '@/components/features/batches/form-elements/LastEditedByInput.vue'
import EditRollsList from '@/components/features/batches/form-elements/EditRollsList.vue'
import { Icon } from '@iconify/vue'
import {computed, onMounted, ref, watch} from 'vue'
import type {Product} from "@/types/product-types";
import {getProductList} from "@/api/products";
import MergeNumberItem from "@/components/features/batches/form-elements/MergeNumberItem.vue";
import Button from 'primevue/button'
import Divider from 'primevue/divider'
import { getRollsByPreviousBatchId } from '@/api/rolls'

const props = defineProps<{
  disabled: boolean
  isHistorical?: boolean
}>()

const model = defineModel<Batch>({
  required: true
})

const currentBatchRolls = computed(() =>
    model.value.rolls?.filter((roll: BatchRoll) => roll.isActive) || []
)

const movedRolls = ref<BatchRoll[]>([])

const currentRollCount = computed(() => currentBatchRolls.value.length)
const movedRollCount = computed(() => movedRolls.value.length)

const qualities = ref<Product[]>();
const qualityName = computed(() => {
  return qualities.value?.find((quality) => quality.id == model.value.finishingQualityId)?.finishedReference || 'Unknown Quality';
})

const showMovedRolls = ref(false)

const movedRollsByState = computed(() => {
  const grouped: { [key: number]: BatchRoll[] } = {}

  movedRolls.value.forEach(roll => {
    if (!grouped[roll.rollState]) {
      grouped[roll.rollState] = []
    }
    grouped[roll.rollState].push(roll)
  })

  return grouped
})

const getStateDescription = (rollState: number) => {
  switch (rollState) {
    case 0: return 'At Facility (Greig)'
    case 1: return 'Sent to Supplier'
    case 2: return 'Finished'
    case 3: return 'Available'
    case 4: return 'Dispatched'
    default: return 'Unknown State'
  }
}

const getBatchStateDescription = (batchState: number) => {
  switch (batchState) {
    case 0: return 'Greig'
    case 1: return 'Offsite'
    case 2: return 'Finished'
    case 3: return 'Available'
    case 4: return 'Dispatched'
    default: return 'Unknown'
  }
}

const fetchMovedRolls = async () => {
  if (!model.value.id) return

  try {
    const allRollsFromThisBatch = await getRollsByPreviousBatchId(model.value.id)

    const currentRollIds = new Set(model.value.rolls?.map(r => r.id) || [])

    movedRolls.value = allRollsFromThisBatch.filter(roll =>
        !currentRollIds.has(roll.id) && roll.isActive
    )
  } catch (error) {
    console.error('Error fetching moved rolls:', error)
    movedRolls.value = []
  }
}

watch(() => model.value.id, (newId) => {
  if (newId) {
    fetchMovedRolls()
  }
}, { immediate: true })

onMounted(async () => {
  qualities.value = await getProductList();
})

</script>

<template>
  <div class="col-span-12 flex items-center gap-5 mb-5">
    <span class="text-lg font-bold">Rolls Sent to Supplier ({{ currentRollCount }})</span>

    <RouterLink
        :to="`/productSpecification/${model.finishingQualityId}`"
        class="flex gap-2 items-center bg-green-100 text-green-600 py-1 px-3 rounded-lg hover:bg-green-200 transition-colors"
    >
      <Icon icon="mdi:fabric" class="text-xl" />
      {{ qualityName }}
    </RouterLink>
  </div>

  <div class="grid grid-cols-12 gap-20 gap-y-4">
    <EditRollsList
        class="col-span-12 mb-4"
        v-model="currentBatchRolls"
        :disabled="disabled"
        :includeInactiveRolls="false"
        :current-batch-number="model.batchNumber"
    />

    <div class="col-span-12" v-if="movedRollCount > 0">
      <div class="flex items-center gap-4 mb-4">
        <Button
            @click="showMovedRolls = !showMovedRolls"
            severity="secondary"
            outlined
            size="small"
            class="min-w-44 text-xs gap-2"
        >
          <template v-if="!showMovedRolls">
            <span>Show Moved Rolls</span>
            <Icon icon="mdi:eye" class="text-xl rounded" />
          </template>
          <template v-else>
            <Icon icon="mdi:eye-off" class="text-xl rounded" />
            <span>Hide Moved Rolls</span>
          </template>
        </Button>

        <span class="text-sm text-gray-600">
          ({{ movedRollCount }} rolls moved from this batch)
        </span>

        <Divider class="!my-3 flex-1" />

        <Button
            @click="showMovedRolls = !showMovedRolls"
            rounded
            size="small"
            class="!bg-primary-50 !border-primary-50 !text-primary-500 !w-8 !h-8 !p-0"
        >
          <Icon v-if="!showMovedRolls" icon="mdi:chevron-down" class="text-4xl" />
          <Icon v-else icon="mdi:chevron-up" class="text-4xl" />
        </Button>
      </div>

      <div v-if="showMovedRolls" class="bg-gray-50 p-4 rounded-lg">
        <h3 class="text-md font-semibold mb-3 text-gray-700">
          Rolls Originally from This Batch
        </h3>
        <p class="text-sm text-gray-600 mb-4">
          These rolls were originally part of this batch but have been moved to other batches.
        </p>

        <div v-for="(rolls, state) in movedRollsByState" :key="state" class="mb-6 last:mb-0">
          <h4 class="text-sm font-medium mb-2 text-gray-600 flex items-center gap-2">
            <Icon
                :icon="state == 0 ? 'mdi:home' : state == 2 ? 'mdi:check-circle' : state == 3 ? 'mdi:package-variant' : state == 4 ? 'mdi:send' : 'mdi:truck'"
                class="text-lg"
            />
            {{ getStateDescription(Number(state)) }} ({{ rolls.length }} rolls)
          </h4>

          <EditRollsList
              :model-value="rolls"
              :disabled="true"
              :includeInactiveRolls="false"
              :showActions="false"
              :current-batch-number="model.batchNumber"
              class="ml-4"
          />
        </div>
      </div>
    </div>

    <MergeNumberItem class="col-span-12 !mb-8" :mergeIds="model.batchMerges"/>
    <SupplierInput class="col-span-6" input-name="finishingSupplierId" v-model="model.finishingSupplierId" :disabled="disabled" />
    <SupplierInput class="col-span-6" input-name="knittingSupplierId" v-model="model.knittingSupplierId" :disabled="disabled" />
    <DateKnittedInput class="col-span-6" input-name="dateKnitted" v-model="model.dateKnitted" :disabled="disabled" />
    <WeightInput class="col-span-6" input-name="weight" v-model="model.weight" :disabled="disabled"/>
    <MachineNumberInput class="col-span-6" input-name="machineNumber" v-model="model.machineNumber" :disabled="disabled" />
    <BayInput class="col-span-6" input-name="bay" v-model="model.bay" :disabled="disabled"/>
    <PurchaseOrderReferenceInput class="col-span-6" input-name="purchaseOrderReference" v-model="model.purchaseOrderReference" :disabled="disabled" />
    <NotesInput class="col-span-12" input-name="notes" v-model="model.notes" :disabled="disabled"/>
    <LastEditedByInput class="col-span-6" />
  </div>
</template>

<style scoped>

</style>